import type { DriverBehaviourScore } from '@chukua/common';
import { behaviourBand, describeBreakdownLine, formatScoreDate } from '../policies/behaviour-labels';

/**
 * The driver's behaviour score, worked out hourly from the movement alerts on their trips (gitaumoses4/chukua#107). Read-only: nothing
 * here can be edited. It says what it is based on and which incidents cost points, so it reads as evidence rather than a verdict.
 */
export function DriverBehaviourPanel({ score }: { score: DriverBehaviourScore }) {
  return (
    <section aria-label="Behaviour score" className="rounded-lg border border-line bg-surface px-4 py-3">
      <p className="text-sm font-medium text-fg">Behaviour score</p>
      <ScoreBody score={score} />
    </section>
  );
}

function ScoreBody({ score }: { score: DriverBehaviourScore }) {
  if (score.available) return <AvailableScore score={score} />;
  if (score.reason === 'not_enough_trips') {
    return (
      <>
        <p className="text-sm text-fg">Not enough trips yet</p>
        <p className="text-sm text-muted">
          Needs at least {score.minTrips} trips in the last {score.windowDays} days (has {score.tripsCounted}).
        </p>
      </>
    );
  }
  return (
    <>
      <p className="text-sm text-muted">Not yet available</p>
      <p className="text-sm text-muted">The score is worked out hourly.</p>
    </>
  );
}

function AvailableScore({ score }: { score: Extract<DriverBehaviourScore, { available: true }> }) {
  return (
    <>
      <p className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-fg">{score.score}</span>
        <span className="text-sm text-muted">out of 100 · {behaviourBand(score.score)}</span>
      </p>
      <p className="text-sm text-muted">
        Based on {score.tripsCounted} {score.tripsCounted === 1 ? 'trip' : 'trips'} in the last {score.windowDays} days, updated {formatScoreDate(score.asOf)}
      </p>
      {score.breakdown.length === 0 ? (
        <p className="mt-1 text-sm text-fg">No incidents</p>
      ) : (
        <ul className="mt-1 text-sm text-fg">
          {score.breakdown.map((line) => (
            <li key={line.metric}>{describeBreakdownLine(line)}</li>
          ))}
        </ul>
      )}
    </>
  );
}
